import { FilterActionTypes } from '@app/pcs/actions/filter';
import { LabelActionTypes } from '@app/pcs/actions/label';
import { CreateLabelForm, LabelEditActions, LabelEditActionTypes } from '@app/pcs/actions/label-edit';
import { LabelColor, labelPropertyMapper } from '@app/pcs/models';
import { getColorForLabel, getLabelColor } from '@app/pcs/util/user-label';
import { ngrxWithValdr } from '@app/valdr/ngrx-with-valdr';
import { Action } from '@ngrx/store';
import { createHandler, createReducer, isTrue } from '@util';
import {
  box,
  Boxed,
  createFormGroupState,
  disable,
  FormGroupState,
  formStateReducer,
  reset,
  setValue,
  updateGroup
} from 'ngrx-forms';
import { Color } from 'pcs-component-library';
import * as R from 'ramda';

export interface LabelForm {
  name?: string;
  description?: string;
  color?: Boxed<Color>;
}

const LABEL_FORM_ID = 'LabelForm';

export interface State {
  formState: FormGroupState<LabelForm>;
  displayEditForm: boolean;
}

const initialFormValue = {
  name: null,
  description: null,
  color: box(getColorForLabel(LabelColor.Blue))
};

const initialFormState = createFormGroupState<LabelForm>(LABEL_FORM_ID, initialFormValue);
export const initialState = {
  formState: initialFormState,
  displayEditForm: false
};

const disableFields = updateGroup<LabelForm>({
  name: disable,
  description: disable
});

const initState = (state: State, {payload: {userLabel, valdrConstraints}}: CreateLabelForm): State => {
  const {label, creator} = userLabel;
  const color = getLabelColor(userLabel);
  const userLabelFormValue = {...label, color: box(getColorForLabel(color))};
  const form = isTrue(creator)
    ? createFormGroupState<LabelForm>(LABEL_FORM_ID, userLabelFormValue)
    : disableFields(createFormGroupState<LabelForm>(LABEL_FORM_ID, userLabelFormValue));

  return {
    displayEditForm: false,
    formState: ngrxWithValdr(labelPropertyMapper, valdrConstraints)(form)
  };
};

const toggleEditForm = (state: State): State =>
  R.evolve({
    displayEditForm: R.not
  }, state);

const resetState = (state: State): State => ({
  displayEditForm: false,
  formState: setValue(reset(state.formState), initialFormValue)
});

const stateReducer = createReducer<State, LabelEditActions>([
  createHandler(LabelEditActionTypes.CreateLabelForm, initState),
  createHandler(LabelEditActionTypes.ResetState, resetState),
  createHandler(LabelActionTypes.SelectLabel, resetState),
  createHandler(FilterActionTypes.SelectFilter, resetState),
  createHandler(LabelEditActionTypes.ToggleEditLabelForm, toggleEditForm)
]);

export const reducer = (state = initialState, action: Action) => {
  const nextState = stateReducer(state, <LabelEditActions>action);
  const updatedFormState = formStateReducer(nextState.formState, action);

  return updatedFormState !== state.formState ? {
    ...nextState,
    formState: ngrxWithValdr(labelPropertyMapper)(updatedFormState)
  } : nextState;
};

